// v1.5.0 · Alfa-Field Connect: orquestador de operaciones de una red de internet por fibra (5 pantallas animadas)
import React from 'react';
import { useTick, Count, Head, Live, Kpi, Status, wave, useReduced } from './kit';

/* 1 · Centro de operaciones de red */
const OLTS = [
  { id: 'OLT Norte', x: 250, y: 50 }, { id: 'OLT Centro', x: 250, y: 120 },
  { id: 'OLT Valle', x: 250, y: 190, warn: true }, { id: 'OLT Sur', x: 250, y: 260 },
];
const EVENTS = [
  ['ok', 'ONT activada, Cumbayá'], ['info', 'OT-1048 asignada a C. Vega'], ['warn', 'Potencia baja, Valle −26,1 dBm'],
  ['ok', 'Corte resuelto, La Carolina'], ['info', 'Ruta optimizada, 6 técnicos'], ['ok', 'ONT activada, Quitumbe'],
];
export function AlfaRed() {
  const t = useTick(1700);
  const reduced = useReduced();
  const onts = 12483 + t * 3 + Math.round(wave(t, 1.3, 4));
  return (
    <div className="flex h-full flex-col gap-3 p-3">
      <Head title="Centro de operaciones de red" sub="Red de fibra, Quito y valles" right={<Live />} />
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Kpi label="ONT en línea"><Count value={onts} /></Kpi>
        <Kpi label="OLT activas" tone="ok"><Count value={14} /></Kpi>
        <Kpi label="Alarmas abiertas" tone="warn"><Count value={t % 4 === 3 ? 1 : 2} /></Kpi>
        <Kpi label="Latencia promedio"><Count value={8 + (t % 3) * 0.4} dec={1} suffix=" ms" /></Kpi>
      </div>
      <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 sm:grid-cols-[1.6fr_1fr]">
        <div className="dash-panel relative min-h-0 overflow-hidden">
          <svg viewBox="0 0 520 310" className="h-full w-full" preserveAspectRatio="xMidYMid meet" role="img" aria-label="Topología de la red: núcleo, 4 OLT y clústeres de clientes">
            <defs>
              <radialGradient id="core-g"><stop offset="0" stopColor="#57D3FF" stopOpacity=".6" /><stop offset="1" stopColor="#57D3FF" stopOpacity="0" /></radialGradient>
            </defs>
            {OLTS.map((o) => (
              <g key={o.id}>
                <path id={`l-${o.id}`} d={`M80 155 C 160 155, 170 ${o.y}, ${o.x} ${o.y}`} fill="none" stroke="rgba(87,211,255,.35)" strokeWidth="1.5" className="flow" />
                {[0, 1, 2].map((k) => (
                  <path key={k} d={`M${o.x} ${o.y} L ${410 + k * 30} ${o.y - 18 + k * 18}`} fill="none" stroke="rgba(87,211,255,.18)" strokeWidth="1" />
                ))}
                {!reduced && (
                  <circle r="3" fill="#A6ECFF">
                    <animateMotion dur={`${2.2 + o.y / 300}s`} repeatCount="indefinite" path={`M80 155 C 160 155, 170 ${o.y}, ${o.x} ${o.y}`} />
                  </circle>
                )}
                <g className={o.warn ? 'blink' : ''}>
                  <rect x={o.x - 44} y={o.y - 13} width="88" height="26" rx="6" fill="#0E2A3C" stroke={o.warn ? '#E9A23B' : 'rgba(87,211,255,.5)'} />
                  <text x={o.x} y={o.y + 4} textAnchor="middle" fontSize="11" fontWeight="600" fill={o.warn ? '#F2C266' : '#DDF0F7'}>{o.id}</text>
                </g>
                {[0, 1, 2].map((k) => (
                  <circle key={k} cx={410 + k * 30} cy={o.y - 18 + k * 18} r="5" fill={o.warn && k === 2 ? '#E9A23B' : '#4FBF6A'} opacity=".85" />
                ))}
              </g>
            ))}
            <circle cx="80" cy="155" r="46" fill="url(#core-g)" />
            <circle cx="80" cy="155" r="22" fill="#0E2A3C" stroke="#57D3FF" strokeWidth="2" />
            <circle cx="80" cy="155" r="22" fill="none" stroke="#57D3FF" className="ring" />
            <text x="80" y="198" textAnchor="middle" fontSize="11" fill="#86A6B6">Núcleo</text>
            <text x="440" y="300" textAnchor="middle" fontSize="10.5" fill="#86A6B6">Clientes por clúster</text>
          </svg>
        </div>
        <ul className="dash-panel hidden min-h-0 flex-col gap-2 overflow-hidden p-3 sm:flex" aria-label="Eventos de la red">
          <li className="dim text-[11px] font-semibold">Eventos recientes</li>
          {[0, 1, 2, 3, 4].map((k) => {
            const [tone, txt] = EVENTS[(t + k) % EVENTS.length];
            return (
              <li key={`${t}-${k}`} className="rise flex items-center justify-between gap-2 text-[12px]" style={{ '--d': `${k * 60}ms` }}>
                <span className="truncate">{txt}</span>
                <Status tone={tone}>{tone === 'ok' ? 'OK' : tone === 'warn' ? 'Revisar' : 'Info'}</Status>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

/* 2 · Órdenes de trabajo que avanzan solas por las columnas */
const COLS = ['Agendada', 'En ruta', 'En sitio', 'Cerrada'];
const KINDS = ['Instalación de fibra', 'Cambio de ONT', 'Visita técnica', 'Traslado de servicio', 'Soporte técnico'];
const ZONES = ['Cumbayá', 'La Carolina', 'Quitumbe', 'Conocoto', 'El Batán', 'Tumbaco', 'Carcelén'];
export function AlfaOrdenes() {
  const t = useTick(1500);
  const cards = [];
  for (let k = t - 3; k <= t + 4; k++) {
    const age = t - k + 4;
    cards.push({ id: 1040 + k, stage: Math.min(3, Math.floor(age / 2)), kind: KINDS[((k % 5) + 5) % 5], zone: ZONES[((k % 7) + 7) % 7] });
  }
  return (
    <div className="flex h-full flex-col gap-3 p-3">
      <Head title="Órdenes de trabajo" sub="Agendamiento por zona y sucursal"
            right={<div className="text-right"><div className="text-[13px] font-bold"><Count value={96.8 + (t % 3) * 0.1} dec={1} suffix=" %" /></div><div className="dim text-[10.5px]">cumplimiento de SLA</div></div>} />
      <div className="grid min-h-0 flex-1 grid-cols-2 gap-2 sm:grid-cols-4">
        {COLS.map((c, ci) => {
          const list = cards.filter((x) => x.stage === ci);
          return (
            <div key={c} className="dash-panel flex min-h-0 flex-col gap-2 overflow-hidden p-2">
              <div className="flex items-baseline justify-between text-[11.5px] font-bold">
                <span className={ci === 3 ? 'text-[#8BE07F]' : ''}>{c}</span>
                <span className="dim num">{ci === 3 ? 18 + t : list.length + (ci === 0 ? 4 : 0)}</span>
              </div>
              {list.map((x) => (
                <div key={x.id} className="rise rounded-md border border-white/10 bg-[#0F2C3F] p-2 text-[11px] leading-snug">
                  <div className="font-semibold">{x.kind}</div>
                  <div className="dim">OT-{x.id}, {x.zone}</div>
                  {ci === 1 && <div className="mt-1.5 h-1 overflow-hidden rounded bg-white/10"><div className="grow-x h-full w-2/3 rounded bg-[#2E9BD6]" /></div>}
                  {ci === 3 && <div className="mt-1 text-[10px] font-semibold text-[#8BE07F]">● Cerrada con foto y GPS</div>}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* 3 · Flota de técnicos en vivo */
const ROUTES = [
  { n: 'J. Paredes', d: 'M40 240 C 120 200, 160 120, 260 110 S 420 80, 470 60', eta: 12 },
  { n: 'D. Andrade', d: 'M60 60 C 140 90, 220 160, 320 170 S 440 220, 500 250', eta: 7 },
  { n: 'C. Vega', d: 'M30 150 C 110 150, 190 230, 300 250 S 420 260, 480 200', eta: 19 },
  { n: 'M. Torres', d: 'M200 20 C 210 90, 260 150, 250 220 S 280 280, 300 300', eta: 4 },
];
export function AlfaFlota() {
  const t = useTick(1000);
  const reduced = useReduced();
  return (
    <div className="grid h-full grid-cols-1 gap-3 p-3 sm:grid-cols-[1.7fr_1fr]">
      <div className="dash-panel relative min-h-0 overflow-hidden">
        <svg viewBox="0 0 520 310" className="h-full w-full" preserveAspectRatio="xMidYMid meet" role="img" aria-label="Mapa con técnicos en movimiento">
          <path d="M0 0 L 230 0 L 200 130 L 0 160 Z" fill="rgba(46,155,214,.08)" stroke="rgba(46,155,214,.35)" strokeDasharray="4 4" />
          <path d="M230 0 L 520 0 L 520 140 L 210 150 Z" fill="rgba(139,107,224,.07)" stroke="rgba(139,107,224,.35)" strokeDasharray="4 4" />
          <path d="M0 160 L 210 150 L 520 140 L 520 310 L 0 310 Z" fill="rgba(199,129,31,.06)" stroke="rgba(199,129,31,.35)" strokeDasharray="4 4" />
          <text x="14" y="22" fontSize="11" fill="#8CCBEA">Zona norte</text>
          <text x="506" y="22" textAnchor="end" fontSize="11" fill="#B9A6F2">Zona valles</text>
          <text x="14" y="296" fontSize="11" fill="#E0B067">Zona sur</text>
          {ROUTES.map((r, i) => (
            <g key={r.n}>
              <path d={r.d} fill="none" stroke="rgba(221,240,247,.12)" strokeWidth="7" strokeLinecap="round" />
              <path d={r.d} fill="none" stroke="#57D3FF" strokeWidth="1.5" className="flow" opacity=".55" />
              <circle r="7" fill="#57D3FF" stroke="#071925" strokeWidth="2.5">
                {!reduced && <animateMotion dur={`${9 + i * 2}s`} repeatCount="indefinite" path={r.d} rotate="auto" />}
              </circle>
            </g>
          ))}
        </svg>
        <div className="absolute bottom-3 left-3"><Live label="6 técnicos en ruta" /></div>
      </div>
      <ul className="dash-panel hidden flex-col gap-2.5 p-3 sm:flex" aria-label="Llegadas estimadas">
        <li className="dim text-[11px] font-semibold">Llegada estimada</li>
        {ROUTES.map((r, i) => {
          const eta = Math.max(1, r.eta - (t % (r.eta + 1)));
          return (
            <li key={r.n} className="flex items-center justify-between gap-2 text-[12.5px]">
              <span className="font-semibold">{r.n}</span>
              <span className="num">{eta} min</span>
              {i === 3 ? <Status tone="ok">En sitio pronto</Status> : <Status tone="info">En ruta</Status>}
            </li>
          );
        })}
        <li className="mt-auto rounded-md bg-[rgba(46,155,214,.12)] p-2 text-[11.5px] leading-snug">El cliente recibe un aviso cuando su técnico sale y cuando está a 5 minutos.</li>
      </ul>
    </div>
  );
}

/* 4 · Provisionamiento de ONT (activación del servicio) */
const STEPS = ['Serial validado', 'Perfil asignado', 'Potencia óptica', 'Sincronizando', 'Servicio activo'];
const LOGS = ['> validar serial ZTEG-C8A12F04 … OK', '> asignar VLAN 100, plan 300 Mbps … OK', '> medir potencia RX … −18,4 dBm',
  '> sincronizar con OLT Norte, puerto 1/3/7 …', '> servicio activo, cliente notificado'];
export function AlfaProvision() {
  const t = useTick(1300);
  const step = t % 7 > 5 ? 5 : t % 7;
  const ang = -90 + ((28 - 18.4) / 20) * 180;
  return (
    <div className="grid h-full grid-cols-1 gap-3 p-3 sm:grid-cols-[1fr_1fr]">
      <div className="dash-panel flex min-h-0 flex-col gap-3 p-3">
        <Head title="Activación de servicio" sub="Cliente: Paredes, Lucía, Cumbayá" right={step >= 5 ? <Status tone="ok">Activo</Status> : <Status tone="info">En curso</Status>} />
        <ol className="flex flex-col gap-2">
          {STEPS.map((s, i) => {
            const done = i < step; const now = i === step;
            return (
              <li key={s} className="flex items-center gap-2.5 text-[12.5px]">
                <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold transition-colors duration-300 ${
                  done ? 'bg-[#4FBF6A] text-[#04121C]' : now ? 'border-2 border-[#57D3FF] text-[#57D3FF]' : 'border border-white/20 text-white/40'}`}>
                  {done ? '✓' : i + 1}
                </span>
                <span className={done ? '' : now ? 'font-semibold text-white' : 'dim'}>{s}</span>
              </li>
            );
          })}
        </ol>
        <div className="mt-auto h-1.5 overflow-hidden rounded bg-white/10">
          <div className="bar-live-x h-full rounded bg-[#57D3FF]" style={{ transform: `scaleX(${Math.min(1, step / 5)})` }} />
        </div>
      </div>
      <div className="flex min-h-0 flex-col gap-3">
        <div className="dash-panel p-3">
          <div className="dim text-[11px] font-semibold">Potencia óptica recibida</div>
          <svg viewBox="0 0 200 132" className="mx-auto h-[124px] w-full max-w-[240px]" role="img" aria-label="Potencia óptica −18,4 dBm, dentro del rango">
            <path d="M20 100 A 80 80 0 0 1 180 100" fill="none" stroke="rgba(255,255,255,.1)" strokeWidth="12" strokeLinecap="round" />
            <path d="M20 100 A 80 80 0 0 1 166 56" fill="none" stroke="#4FBF6A" strokeWidth="12" strokeLinecap="round" pathLength="1" className="draw" />
            <g style={{ transform: `rotate(${step >= 2 ? ang : -90}deg)`, transformOrigin: '100px 100px', transition: 'transform 900ms cubic-bezier(.23,1,.32,1)' }}>
              <line x1="100" y1="100" x2="100" y2="34" stroke="#DDF0F7" strokeWidth="2.5" strokeLinecap="round" />
            </g>
            <circle cx="100" cy="100" r="5" fill="#DDF0F7" />
            <text x="100" y="128" textAnchor="middle" fontSize="15" fontWeight="800" fill="#DDF0F7">{step >= 2 ? '−18,4 dBm' : '…'}</text>
          </svg>
          <div className="dim flex justify-between text-[10.5px]"><span>−28 dBm</span><span>rango aceptado −8 a −25</span><span>−8</span></div>
        </div>
        <div className="dash-panel min-h-0 flex-1 overflow-hidden p-3 font-mono text-[11px] leading-[1.7] text-[#9FE6FF]" aria-label="Registro de la activación">
          {LOGS.slice(0, Math.min(step + 1, 5)).map((l, i) => (
            <div key={i} className={`rise truncate ${i === step ? 'caret' : ''}`}>{l}</div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* 5 · Inventario de equipos por bodega, con número de serie */
const BODEGAS = [
  { n: 'Quito Norte', ont: 84, rtr: 61, fib: 72 }, { n: 'Quito Sur', ont: 66, rtr: 48, fib: 55 },
  { n: 'Valle', ont: 18, rtr: 40, fib: 63, low: true }, { n: 'Cumbayá', ont: 52, rtr: 35, fib: 41 },
];
export function AlfaInventario() {
  const t = useTick(1400);
  const scans = ['ZTEG-C8A12F04', 'HWTC-77B10E21', 'ZTEG-C8A1309A', 'HWTC-77B11C02', 'ZTEG-C8A14D17'];
  const techs = ['J. Paredes', 'C. Vega', 'D. Andrade', 'M. Torres', 'A. Ruiz'];
  return (
    <div className="grid h-full grid-cols-1 gap-3 p-3 sm:grid-cols-[1.4fr_1fr]">
      <div className="dash-panel flex min-h-0 flex-col gap-3 p-3">
        <Head title="Equipos por bodega" sub="Stock con número de serie, sincronizado con Odoo"
              right={<div className="flex gap-3 text-[10.5px]"><span className="flex items-center gap-1"><i className="h-2 w-2 rounded-sm bg-[#2E9BD6]" />ONT</span><span className="flex items-center gap-1"><i className="h-2 w-2 rounded-sm bg-[#C7811F]" />Router</span><span className="flex items-center gap-1"><i className="h-2 w-2 rounded-sm bg-[#8B6BE0]" />Fibra</span></div>} />
        <div className="grid min-h-0 flex-1 grid-cols-4 items-end gap-3">
          {BODEGAS.map((b, i) => {
            const ont = b.ont - (t % 6) * (i === 2 ? 1 : 0.5);
            return (
              <div key={b.n} className="flex h-full min-w-0 flex-col justify-end">
                <div className="flex h-[150px] items-end justify-center gap-[2px]">
                  {[[ont, '#2E9BD6'], [b.rtr, '#C7811F'], [b.fib, '#8B6BE0']].map(([v, c], k) => (
                    <div key={k} className="bar-live w-[26%] rounded-t-[4px]" style={{ height: '100%', background: c, transform: `scaleY(${v / 100})` }} title={`${Math.round(v)}`} />
                  ))}
                </div>
                <div className="mt-2 truncate text-center text-[11px] font-semibold">{b.n}</div>
                {b.low && <div className="mt-1 flex justify-center"><Status tone="warn">ONT bajo mínimo</Status></div>}
              </div>
            );
          })}
        </div>
      </div>
      <ul className="dash-panel hidden min-h-0 flex-col gap-2 overflow-hidden p-3 sm:flex" aria-label="Movimientos escaneados">
        <li className="dim text-[11px] font-semibold">Escaneos de salida</li>
        {[0, 1, 2, 3, 4].map((k) => {
          const i = (t + k) % scans.length;
          return (
            <li key={`${t}-${k}`} className="rise rounded-md border border-white/10 bg-[#0F2C3F] px-2.5 py-2 text-[11.5px]" style={{ '--d': `${k * 50}ms` }}>
              <div className="num font-semibold">{scans[i]}</div>
              <div className="dim">ONT entregada a {techs[i]}</div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export const alfaScreens = [
  { t: 'Red', C: AlfaRed }, { t: 'Órdenes', C: AlfaOrdenes }, { t: 'Flota', C: AlfaFlota },
  { t: 'Activación', C: AlfaProvision }, { t: 'Inventario', C: AlfaInventario },
];
